import clsx from "clsx";
import SensorStatusBoardStyle from "./css/SensorStatusBoard.module.css";
import Card from "../../../shared/components/Card";
import Flex from "../../../shared/components/Flex";
import Text from "../../../shared/components/Text";
import Close from "../../../shared/components/Close";
import useDevice from "../../../shared/hooks/useDevice";
import React, { useEffect, useState, useRef, useMemo } from "react";
import SensorStatusBoardItem from "./SensorStatusBoardItem";
import { useAppDispatch, useAppSelector } from "../../../shared/store/store";
import { setFilter, setSearchKeyword, setSelectedSensor, setAnimationOrigin } from "../slice/sensorDashboardSlice";
import SensorStatusBoardDetail from "./SensorStatusBoardDetail";
import { debounce } from "../../../shared/utils/debounce";
import { useGetSensorDataQuery } from "../api/sensorDataApi";
import useGetStatusMessage from "../hooks/useStatusFilter";
import { fromLonLat } from 'ol/proj';
import ImgFolder from "../../../assets/img/img_folder.png";
import Seperator from "../../../shared/components/Seperator";
import { SENSOR_DATA_POLLING_INTERVAL } from "../constants/pollingConfig";

type SensorStatusBoardProps = {
    isOpen?: boolean;
    setOpen: (open: boolean) => void;
};

function SensorStatusBoard({
    isOpen,
    setOpen,
}: SensorStatusBoardProps) {
    const device = useDevice();
    const sensorDashboardFilter = useAppSelector(state => state.sensorDashboard.filter);
    const searchKeyword = useAppSelector(state => state.sensorDashboard.searchKeyword);
    const selectedSensor = useAppSelector(state => state.sensorDashboard.selectedSensor);
    const mapExtent = useAppSelector(state => state.sensorDashboard.mapExtent);
    const animationOrigin = useAppSelector(state => state.sensorDashboard.animationOrigin);
    const focusIndex = useAppSelector(state => state.sensorDashboard.focusIndex);
    const sensorData = useAppSelector(state => state.sensorData);
    const dispatch = useAppDispatch();
    const { processingMap } = useGetStatusMessage();
    
    // API 호출
    const { data: rawSensorData, isFetching, isSuccess } = useGetSensorDataQuery({
        // status: processingMap.get(sensorDashboardFilter) ?? "",
        // searchKeyword: searchKeyword,
    }, {
        refetchOnFocus: true,
        refetchOnMountOrArgChange: true,
        refetchOnReconnect: true,
        pollingInterval: SENSOR_DATA_POLLING_INTERVAL
    });
    
    // extent 내에 있는지 확인하는 함수
    const isInExtent = (sensorLot: number, sensorLat: number, extent: [number, number, number, number]) => {
        const [x, y] = fromLonLat([sensorLot, sensorLat]);
        const [minX, minY, maxX, maxY] = extent;
        return x >= minX && x <= maxX && y >= minY && y <= maxY;
    };

    // 상태별 정렬 우선순위 (점검 > 경보 > 주의 > 안전)
    const statusOrder: Record<string, number> = {
        "점검": 0,
        "경보": 1,
        "주의": 2,
        "안전": 3,
    };

    // 제품 데이터 정렬 함수
    const sortSensorData = (data: any[]) => {
        return [...data].sort((a, b) => {
            const statusA = processingMap.get(a.sensorStatusGrp) ?? "";
            const statusB = processingMap.get(b.sensorStatusGrp) ?? "";
            const orderA = statusOrder[statusA] ?? 999;
            const orderB = statusOrder[statusB] ?? 999;
            return orderA - orderB;
            // TODO: 각 카테고리 내 추가 정렬 규칙은 여기에 추가
        });
    };

    // 이전 데이터를 유지하면서 새 데이터가 들어오면 업데이트
    const [sensorStatusBoardItems, setSensorStatusBoardItems] = useState<any[]>([]);
    const filteredAndSortedItems = useMemo(() => {
        // 지도 API에서 받은 데이터가 비어 있으면 store의 센서 데이터로 대체해 깜빡임 방지
        const baseData = rawSensorData && rawSensorData.length > 0 ? rawSensorData : sensorData;
        if (!baseData || baseData.length === 0) return [];

        let filteredData = baseData;
        
        // 카테고리 필터링 (유효한 필터만 적용, 지도합계/전체는 미적용)
        const normalizedFilter = processingMap.get(sensorDashboardFilter) ?? sensorDashboardFilter;
        const validStatusNames = new Set(["지도합계", "안전", "주의", "경보", "점검"]);
        const validStatusCodes = new Set(["SG001", "SG002", "SG003", "SG004"]);
        const shouldFilter =
            normalizedFilter &&
            normalizedFilter !== "지도합계" &&
            (validStatusNames.has(normalizedFilter) || validStatusCodes.has(normalizedFilter));

        if (shouldFilter) {
            filteredData = filteredData.filter(sensor => {
                const statusName = processingMap.get(sensor.sensorStatusGrp) ?? sensor.sensorStatusGrp;
                return statusName === normalizedFilter || sensor.sensorStatusGrp === normalizedFilter;
            });
        }
        
        // 지도 범위 필터링
        if (mapExtent) {
            filteredData = filteredData.filter(sensor =>
                isInExtent(sensor.sensorLot, sensor.sensorLat, mapExtent)
            );
        }
        return sortSensorData(filteredData);
    }, [rawSensorData, sensorData, mapExtent, sensorDashboardFilter, processingMap]);

    useEffect(() => {
        setSensorStatusBoardItems((prev) => {
            // 로딩 중에 빈 배열로 덮어쓰지 않도록 보호
            if (isFetching && filteredAndSortedItems.length === 0) {
                return prev;
            }
            const isSame =
                prev.length === filteredAndSortedItems.length &&
                prev.every((item, idx) => item?.imei === filteredAndSortedItems[idx]?.imei);
            return isSame ? prev : filteredAndSortedItems;
        });
        setIsTyping(false); // 데이터 로드 완료 시 타이핑 상태 해제
    }, [filteredAndSortedItems, isFetching]);

    // 필터 변경 시 타이핑 상태 해제
    useEffect(() => {
        setIsTyping(false);
    }, [sensorDashboardFilter]);
    
    // 드래그 상태 관리
    const [currentTranslateY, setCurrentTranslateY] = useState(0);
    const [isClosing, setIsClosing] = useState(false);
    const [isPreBounce, setIsPreBounce] = useState(false); // 스프링 효과를 위한 프리바운스
    const [isOpening, setIsOpening] = useState(false); // 열리는 애니메이션 상태
    const [isTyping, setIsTyping] = useState(false); // 디바운싱 대기 중 상태
    const cardRef = useRef<HTMLDivElement>(null);
    const translateYRef = useRef(0); // 최신 translateY 값을 추적
    const dragStateRef = useRef({
        isDragging: false,
        startY: 0,
    });
    const prevIsOpenRef = useRef(isOpen); // 이전 isOpen 상태 추적
    
    // 데스크탑 애니메이션 상태 관리
    const [desktopScale, setDesktopScale] = useState(isOpen ? 1 : 0); // scale 값 (0: 닫힘, 1: 열림) - 초기값을 isOpen에 맞게 설정
    const [isDesktopAnimating, setIsDesktopAnimating] = useState(false);
    const prevFilterRef = useRef(sensorDashboardFilter); // 필터 변경 감지용
    const animationTimerRef = useRef<NodeJS.Timeout | null>(null); // 애니메이션 타이머 추적
    const animationRafRef = useRef<number | null>(null); // requestAnimationFrame 추적
    const animationKeyRef = useRef(0); // 애니메이션 고유 키 (중복 실행 방지)
    
    // isOpen 상태에 따라 desktopScale 초기화 (애니메이션 없이)
    useEffect(() => {
        if (device !== "desktop") return;
        
        // animationOrigin이 없고 애니메이션 중이 아닐 때만 초기화
        if (!animationOrigin && !isDesktopAnimating) {
            setDesktopScale(isOpen ? 1 : 0);
        }
    }, [isOpen, device, animationOrigin, isDesktopAnimating]);

    const sensorStatusBoardItemList = sensorStatusBoardItems.map((item, index) => {
        return (
            <React.Fragment key={item.imei}>
                <SensorStatusBoardItem {...item} />
                {index !== sensorStatusBoardItems.length - 1 && <Seperator variant="dashed"/>}
            </React.Fragment>
        )
    });

    // 이전 애니메이션 정리 함수
    const cleanupAnimation = () => {
        if (animationTimerRef.current) {
            clearTimeout(animationTimerRef.current);
            animationTimerRef.current = null;
        }
        if (animationRafRef.current !== null) {
            cancelAnimationFrame(animationRafRef.current);
            animationRafRef.current = null;
        }
    };

    // 데스크탑 애니메이션 처리 (transform-origin 기반)
    useEffect(() => {
        if (device !== "desktop") return;
        
        // animationOrigin이 없으면 애니메이션 중지
        if (!animationOrigin) {
            cleanupAnimation();
            return;
        }
        
        // 이전 애니메이션 정리
        cleanupAnimation();
        
        // 새로운 애니메이션 키 생성
        animationKeyRef.current += 1;
        const currentKey = animationKeyRef.current;
        
        // 닫힘 -> 열림
        if (!prevIsOpenRef.current && isOpen) {
            setIsDesktopAnimating(true);
            setDesktopScale(0); // 초기 상태를 scale(0)으로 설정
            
            // 다음 프레임에서 scale(1)로 애니메이션
            const rafId1 = requestAnimationFrame(() => {
                if (currentKey !== animationKeyRef.current) return; // 다른 애니메이션이 시작되었으면 취소
                
                const rafId2 = requestAnimationFrame(() => {
                    if (currentKey !== animationKeyRef.current) return; // 다른 애니메이션이 시작되었으면 취소
                    
                    setDesktopScale(1);
                    
                    // 애니메이션 완료 후
                    animationTimerRef.current = setTimeout(() => {
                        if (currentKey === animationKeyRef.current) {
                            setIsDesktopAnimating(false);
                            dispatch(setAnimationOrigin(null));
                        }
                    }, 600);
                });
                animationRafRef.current = rafId2;
            });
            animationRafRef.current = rafId1;
        }
        // 열림 -> 닫힘
        else if (prevIsOpenRef.current && !isOpen) {
            setIsDesktopAnimating(true);
            setDesktopScale(1); // 현재 상태 유지
            
            // 다음 프레임에서 scale(0)으로 애니메이션
            const rafId1 = requestAnimationFrame(() => {
                if (currentKey !== animationKeyRef.current) return; // 다른 애니메이션이 시작되었으면 취소
                
                const rafId2 = requestAnimationFrame(() => {
                    if (currentKey !== animationKeyRef.current) return; // 다른 애니메이션이 시작되었으면 취소
                    
                    setDesktopScale(0);
                    
                    // 애니메이션 완료 후
                    animationTimerRef.current = setTimeout(() => {
                        if (currentKey === animationKeyRef.current) {
                            setIsDesktopAnimating(false);
                            dispatch(setAnimationOrigin(null));
                        }
                    }, 600);
                });
                animationRafRef.current = rafId2;
            });
            animationRafRef.current = rafId1;
        }
        // 스위칭: 이미 열려있는 상태에서 다른 탭 클릭
        // 주의: 닫혀있었다면 위의 "닫힘 -> 열림" 조건에서 처리되므로 여기서는 열려있었을 때만 처리
        else if (prevIsOpenRef.current && isOpen && prevFilterRef.current !== sensorDashboardFilter) {
            setIsDesktopAnimating(true);
            setDesktopScale(1); // 현재 열려있는 상태
            
            // 먼저 닫히는 애니메이션 (scale 1 -> 0)
            const rafId1 = requestAnimationFrame(() => {
                if (currentKey !== animationKeyRef.current) return; // 다른 애니메이션이 시작되었으면 취소
                
                const rafId2 = requestAnimationFrame(() => {
                    if (currentKey !== animationKeyRef.current) return; // 다른 애니메이션이 시작되었으면 취소
                    
                    setDesktopScale(0);
                    
                    // 닫힌 후 열리는 애니메이션 (scale 0 -> 1)
                    animationTimerRef.current = setTimeout(() => {
                        if (currentKey !== animationKeyRef.current) return; // 다른 애니메이션이 시작되었으면 취소
                        
                        const rafId3 = requestAnimationFrame(() => {
                            if (currentKey !== animationKeyRef.current) return; // 다른 애니메이션이 시작되었으면 취소
                            
                            const rafId4 = requestAnimationFrame(() => {
                                if (currentKey !== animationKeyRef.current) return; // 다른 애니메이션이 시작되었으면 취소
                                
                                setDesktopScale(1);
                                
                                // 애니메이션 완료 후
                                animationTimerRef.current = setTimeout(() => {
                                    if (currentKey === animationKeyRef.current) {
                                        setIsDesktopAnimating(false);
                                        dispatch(setAnimationOrigin(null));
                                    }
                                }, 600);
                            });
                            animationRafRef.current = rafId4;
                        });
                        animationRafRef.current = rafId3;
                    }, 300); // 닫히는 애니메이션 시간의 절반
                });
                animationRafRef.current = rafId2;
            });
            animationRafRef.current = rafId1;
        }
        
        // 상태 업데이트: isOpen이 true일 때만 필터 업데이트 (닫혀있을 때는 필터 비교 안 함)
        prevIsOpenRef.current = isOpen;
        if (isOpen) {
            prevFilterRef.current = sensorDashboardFilter;
        }
        
        // cleanup 함수
        return () => {
            cleanupAnimation();
        };
    }, [isOpen, animationOrigin, sensorDashboardFilter, device, dispatch]);

    // isOpen 상태 변경 감지하여 애니메이션 처리
    useEffect(() => {
        if (device === "desktop") return;
        
        // 닫힘 -> 열림
        if (!prevIsOpenRef.current && isOpen) {
            setIsOpening(true);
            setCurrentTranslateY(0);
            translateYRef.current = 0;
            
            // 스프링 애니메이션 완료 후 상태 해제
            setTimeout(() => {
                setIsOpening(false);
            }, 600); // 스프링 애니메이션 시간과 맞춤
        }
        // 열림 -> 닫힘
        else if (prevIsOpenRef.current && !isOpen) {
            setIsClosing(true);
            setCurrentTranslateY(window.innerHeight + 100);
            translateYRef.current = window.innerHeight + 100;
            
            setTimeout(() => {
                setIsClosing(false);
                setCurrentTranslateY(0);
                translateYRef.current = 0;
            }, 700); // 닫히는 애니메이션 시간과 맞춤
        }
        
        prevIsOpenRef.current = isOpen;
    }, [isOpen, device]);

    // 드래그 시작
    const handleDragStart = (clientY: number) => {
        if (device === "desktop") return;
        
        // 현재 DOM의 실제 위치 가져오기 (애니메이션 중일 수 있으므로)
        if (cardRef.current) {
            const computedStyle = window.getComputedStyle(cardRef.current);
            const matrix = new DOMMatrix(computedStyle.transform);
            const currentY = matrix.m42; // transform의 translateY 값
            translateYRef.current = Math.max(0, currentY); // 음수면 0으로
            
            // transition 제거 (부드러운 드래그를 위해)
            cardRef.current.style.transition = 'none';
            // 현재 위치 고정 (애니메이션 중단)
            cardRef.current.style.transform = `translateY(${translateYRef.current}px)`;
        }
        
        dragStateRef.current = {
            isDragging: true,
            startY: clientY - translateYRef.current,
        };
    };
    
    // 드래그 중 (직접 DOM 조작으로 부드러운 움직임)
    const handleDragMove = (clientY: number) => {
        if (!dragStateRef.current.isDragging || device === "desktop") return;
        
        const deltaY = clientY - dragStateRef.current.startY;
        // 원래 위치(0)보다 위로는 못 올라가도록 제한
        const newTranslateY = Math.max(0, deltaY);
        translateYRef.current = newTranslateY;
        
        // 직접 DOM 조작 (React 리렌더링 없이 부드러운 움직임)
        if (cardRef.current) {
            cardRef.current.style.transform = `translateY(${newTranslateY}px)`;
        }
    };
    
    // 드래그 종료
    const handleDragEnd = () => {
        if (!dragStateRef.current.isDragging || device === "desktop") return;
        dragStateRef.current.isDragging = false;
        
        const threshold = 250; // 150px 이상 내리면 닫기
        const finalTranslateY = translateYRef.current;
        
        if (finalTranslateY > threshold) {
            // threshold 넘음: 닫기 (스프링 효과)
            // 1단계: 위로 살짝 올라가는 애니메이션
            if (cardRef.current) {
                cardRef.current.style.transition = 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)';
                cardRef.current.style.transform = `translateY(${finalTranslateY - 100}px)`;
            }
            setIsPreBounce(true);
            setCurrentTranslateY(finalTranslateY - 100);
            translateYRef.current = finalTranslateY - 100;
            
            // 2단계: 위로 올라간 후 아래로 사라지는 애니메이션
            setTimeout(() => {
                setIsPreBounce(false);
                // inline style 정리 후 React가 닫기 애니메이션 처리
                if (cardRef.current) {
                    cardRef.current.style.transition = '';
                    cardRef.current.style.transform = '';
                }
                setOpen(false);
            }, 250); // 위로 올라가는 애니메이션(200ms) 완료 후
        } else {
            // threshold 안 넘음: 원래 위치로 복귀
            if (cardRef.current) {
                cardRef.current.style.transition = 'transform 0.3s ease-out';
                cardRef.current.style.transform = 'translateY(0px)';
            }
            setCurrentTranslateY(0);
            translateYRef.current = 0;
        }
    };
    // if (device !== "desktop") return null;
    const getInitialTranslateY = () => {
        if (!isOpen && !isClosing) {
            return window.innerHeight + 100; // 닫힌 상태에서는 화면 아래로
        }
        return currentTranslateY;
    };

    const notDesktopStyle = {
        transform: `translateY(${getInitialTranslateY()}px)`,
        transition: dragStateRef.current.isDragging 
            ? 'none' 
            : isPreBounce 
                ? 'transform 0.15s ease-out' 
                : isClosing 
                    ? 'transform 0.65s cubic-bezier(0.25, 0.1, 0.25, 1)' // 부드럽게 내려감
                    : isOpening
                        ? 'transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)' // 스프링 효과
                        : 'transform 0.3s ease-out',
        // display는 더 이상 none으로 숨기지 않음
    }

    const handleSearch = (value: string) => {
        dispatch(setSearchKeyword(value));
        setIsTyping(false); // 실제 검색 실행 시 타이핑 상태 해제
    }

    // 디바운스 적용된 검색 함수 (500ms)
    const debouncedSearch = useMemo(
        () => debounce(handleSearch, 500),
        [dispatch]
    );

    // 입력 시작 시 타이핑 상태 활성화
    const handleInputChange = (value: string) => {
        setIsTyping(true);
        debouncedSearch(value);
    };

    function handleFilterClick(filter: string) {
        dispatch(setFilter(filter));
        dispatch(setSelectedSensor(null));
    }

    const isMobile = device === "mobile";

    return (
        <Card
            className={clsx(SensorStatusBoardStyle.container, {
                [SensorStatusBoardStyle.dragging]: dragStateRef.current.isDragging,
                [SensorStatusBoardStyle.preBounce]: isPreBounce,
                [SensorStatusBoardStyle.closing]: isClosing,
            })}
            ref={cardRef}
            width={467+24+24}
            // height={600}
            borderRadius={24}
            shadow={device === "desktop" ? 4 : 12}
            shadowDirection={device === "desktop" ? "bottom" : "default"}
            shadowAlpha={device === "desktop" ? 1 : 0.08}
            style={device !== "desktop" ? notDesktopStyle : {
                display: isOpen ? "block" : "none",
                transformOrigin: animationOrigin ? `${animationOrigin.x}px ${animationOrigin.y}px` : 'center center',
                transform: `scale(${desktopScale})`,
                transition: isDesktopAnimating ? 'transform 0.6s cubic-bezier(0.34, 1.5, 0.64, 1)' : 'none',
            }
        }
        >
            <Flex
                flexDirection="column"
                width={"100%"}
                height={"100%"}
                paddingY={isMobile ? 12 : 24}
                gap={16}
            >

                {device !== "desktop" ? (
                    <SensorStatusBoardDragContainer 
                        onDragStart={handleDragStart}
                        onDragMove={handleDragMove}
                        onDragEnd={handleDragEnd}
                    />
                ) : 
                (<Flex justifyContent="space-between" alignItems="center" paddingX={24}>
                    <Flex gap={8}>
                        <img alt="" src="images/ico_book.png"/>
                        <Text size={"22px"} weight={"bold"}>
                            {selectedSensor ? selectedSensor.sensorAlias: "제품목록"}
                        </Text>
                    </Flex>
                    <Close size="md" onClick={() => {
                        // 카테고리 토글과 동일한 로직: 현재 탭의 위치에서 애니메이션
                        if (device === "desktop") {
                            // focusIndex를 사용해서 현재 탭 요소 찾기
                            const currentTab = document.querySelector(`[data-tab-index="${focusIndex}"]`) as HTMLElement;
                            
                            if (currentTab) {
                                const rect = currentTab.getBoundingClientRect();
                                const centerX = rect.left + rect.width / 2;
                                const centerY = rect.top + rect.height / 2;
                                const boardLeft = 92;
                                const boardTop = 12;
                                const originX = centerX - boardLeft;
                                const originY = centerY - boardTop;
                                
                                dispatch(setAnimationOrigin({ x: originX, y: originY }));
                            }
                        }
                        setOpen(false);
                    }} />
                </Flex>)}
                {/* <Flex gap={10} className={SensorStatusBoardStyle["no-scrollbar"]} style={{overflowX: "auto"}}>
                    <PillButton fullWidth variant={sensorDashboardFilter === "전체" ? "black" : "white"} height={39} paddingX={22} paddingY={10} onClick={() => handleFilterClick("전체")}>
                        전체
                    </PillButton>
                    <PillButton fullWidth variant={sensorDashboardFilter === "안전" ? "black" : "white"} height={39} paddingX={22} paddingY={10} onClick={() => handleFilterClick("안전")}>
                        안전
                    </PillButton>
                    <PillButton fullWidth variant={sensorDashboardFilter === "주의" ? "black" : "white"} height={39} paddingX={22} paddingY={10} onClick={() => handleFilterClick("주의")}>
                        주의
                    </PillButton>
                    <PillButton fullWidth variant={sensorDashboardFilter === "경보" ? "black" : "white"} height={39} paddingX={22} paddingY={10} onClick={() => handleFilterClick("경보")}>
                        경보
                    </PillButton>
                    <PillButton fullWidth variant={sensorDashboardFilter === "점검" ? "black" : "white"} height={39} paddingX={22} paddingY={10} onClick={() => handleFilterClick("점검")}>
                        점검
                    </PillButton>
                </Flex> */}
                {/* <Spacer direction="vertical" size={16} />
                {!selectedSensor && (
                    <>
                        <Input
                            rightIcon={<IcoSearch />}
                            fullWidth
                            placeholder="검색어를 입력해주세요"
                            onChange={(e) => handleInputChange(e.target.value)}
                        />
                        <Spacer direction="vertical" size={16} />
                    </>
                )} */}

                    {
                        <Flex flexDirection="column" gap={16} overflowY="auto" paddingX={isMobile ? 12 : 24} justifyContent={isMobile ? "flex-start" : selectedSensor ? "space-between" : "flex-start"} height={"100%"}>
                            {
                                selectedSensor ? (
                                    <SensorStatusBoardDetail key={selectedSensor.imei} {...selectedSensor} />
                                ) : (
                                    sensorStatusBoardItemList.length > 0 ? 
                                        sensorStatusBoardItemList
                                    :
                                    <Card backgroundColor="#F5F7F9" border={0} shadow={0} height={155}>
                                        <Flex alignItems="center" justifyContent="center" gap={15} flexDirection="column" height="100%">
                                            <img alt="" width={40} height={30} src={ImgFolder}/>
                                            <Text color="#999999" size={16} weight={"medium"}>데이터가 없습니다.</Text>
                                        </Flex>
                                    </Card>
                                )
                            }
                        </Flex>
                    }
                    {/* {
                        selectedSensor ? (
                            <SensorStatusBoardDetail key={selectedSensor.imei} {...selectedSensor} />
                        ) : (isTyping || isFetching) ? ( null
                            // <Flex width={"100%"} height={"100%"} justifyContent="center" alignItems="center">
                            //     <Text size={"16px"} weight={"semibold"}>
                            //         {isTyping ? "검색 중..." : "제품 정보를 불러오고 있습니다."}
                            //     </Text>
                            // </Flex>
                        ) : sensorStatusBoardItemList.length > 0 ? (
                            sensorStatusBoardItemList
                        ) : (
                            <Flex width={"100%"} height={"100%"} justifyContent="center" alignItems="center">
                                <Text size={"16px"} weight={"semibold"}>데이터가 없습니다.</Text>
                            </Flex>
                        )
                    } */}
                {/* <ul className="top_category" data-select="all" onClick={({target}) => {
                    if (target.tagName !== "LI") return;

                    setSelectedCtegory(target.dataset.status);
                    setSearchData({text:searchText,category:target.dataset.status});
                    setSelectedSensor(null);
                }}
                >
                    <li data-status="all" className={selectedCtegory === 'all' ? "all selected":"all"}>전체</li>
                    <li data-status="warning" className={selectedCtegory === 'warning' ? "warning selected":"warning"}>경보</li>
                    <li data-status="caution" className={selectedCtegory === 'caution' ? "caution selected":"caution"}>주의</li>
                    <li data-status="safety" className={selectedCtegory === 'safety' ? "safety selected":"safety"}>안전</li>
                    <li data-status="inspection" className={selectedCtegory === 'inspection' ? "inspection selected":"inspection"}>점검</li>
                </ul> */}
                {/* <input
                        type="text"
                        placeholder="검색어를 입력해주세요"
                        value={searchText}
                        onChange={({target}) => {
                            setSearchText(target.value);
                        }}
                        onKeyPress={(e) => {
                            if (e.key === "Enter") {
                                setReqCnt(1);
                                setSearchData({text:searchText,category:selectedCtegory});
                                setSelectedSensor(null);
                            }
                        }}
                    />
                    <div onClick={() => {
                        setReqCnt(1);
                        setSearchData({text:searchText,category:selectedCtegory});
                        setSelectedSensor(null);
                    }}>검색</div> */}
                {/* <ul className="main_body">
            {selectedSensor && (
              <MapCard
                data={selectedSensor}
                detailOn={true}
                back={() => {
                  setSelectedSensor(null);
                }}
              />
            )}

            {!selectedSensor &&
              sensorData.search?.map((ele) => {
                return (
                  <MapCard
                    onClick={sensorCardClick}
                    key={ele.manageCd}
                    data={ele}
                    move={moveXY}
                    popup={popupOverlay}
                    drag={drag}
                  />
                );
              })}
            {!sensorData.search && (
              <div className="placeholder">검색어를 입력해주세요.</div>
            )}
            {sensorData.search && !sensorData.search.length && (
              <div className="placeholder">데이터가 없습니다.</div>
            )}
          </ul> */}
                {/* <div className="search_utile">
          <div
            className={`${
              isOpen ? `drag open ${drag.searchRef && "on"}` : `drag`
            }`}
            onMouseDown={(e) => {
              if (isOpen) {
                const width = searchRef.current.getBoundingClientRect().width;
                setDrag({
                  x: e.clientX,
                  y: e.clientY,
                  width: width,
                  ...(width > 370 ? { size: "md" } : { size: "sm" }),
                  searchRef: searchRef,
                });
              }
            }}
          ></div>
        </div> */}
            </Flex>
        </Card>
    );
}

type SensorStatusBoardDragContainerProps = {
    onDragStart: (clientY: number) => void;
    onDragMove: (clientY: number) => void;
    onDragEnd: () => void;
};

function SensorStatusBoardDragContainer({ onDragStart, onDragMove, onDragEnd }: SensorStatusBoardDragContainerProps) {
    const [isDraggingUI, setIsDraggingUI] = useState(false);
    
    const handleTouchStart = (e: React.TouchEvent) => {
        e.preventDefault();
        setIsDraggingUI(true);
        // 드래그 시작 시 body 스크롤 막기
        document.body.style.overflow = 'hidden';
        onDragStart(e.touches[0].clientY);
        
        const handleTouchMove = (moveEvent: TouchEvent) => {
            moveEvent.preventDefault(); // 터치 이동 시 스크롤 방지
            onDragMove(moveEvent.touches[0].clientY);
        };
        
        const handleTouchEnd = () => {
            setIsDraggingUI(false);
            // 드래그 종료 시 body 스크롤 복구
            document.body.style.overflow = '';
            onDragEnd();
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
        
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleTouchEnd);
    };
    
    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDraggingUI(true);
        // 드래그 시작 시 body 스크롤 막기
        document.body.style.overflow = 'hidden';
        onDragStart(e.clientY);
        
        const handleMouseMove = (moveEvent: MouseEvent) => {
            moveEvent.preventDefault(); // 마우스 이동 시 기본 동작 방지
            onDragMove(moveEvent.clientY);
        };
        
        const handleMouseUp = () => {
            setIsDraggingUI(false);
            // 드래그 종료 시 body 스크롤 복구
            document.body.style.overflow = '';
            onDragEnd();
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };
    
    return (
        <div 
            className={SensorStatusBoardStyle["drag-container"]}
            onTouchStart={handleTouchStart}
            onMouseDown={handleMouseDown}
            style={{ cursor: isDraggingUI ? 'grabbing' : 'grab' }}
        >
            <div className={SensorStatusBoardStyle["drag-container-inner"]}></div>
        </div>
    )
}

export default SensorStatusBoard;
